import { get, writable } from "svelte/store";

/** Bounds and step for the Lean board's zoom control — 70% still shows a whole
 *  changeover's width without every card collapsing to nothing, 160% is about as
 *  large as a single card needs to get to read from across a desk. */
export const BOARD_ZOOM_MIN = 0.7;
export const BOARD_ZOOM_MAX = 1.6;
const BOARD_ZOOM_STEP = 0.1;
const BOARD_ZOOM_DEFAULT = 1;

const STORAGE_KEY = "crane_board_zoom";

function clamp(v: number): number {
  // Rounded to avoid binary floating-point drift (0.7 + 0.1 + 0.1 + ... landing on
  // 1.0999999999999999) building up across repeated zoom-in/zoom-out presses.
  return Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN, Math.round(v * 100) / 100));
}

/** A personal viewing preference, not a project setting — kept in localStorage, like
 *  `theme`, not synced through PocketBase (unlike allowWeekendWork/subcontractorColors,
 *  which every viewer of the project should see the same way). */
function readStored(): number {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return v > 0 && Number.isFinite(v) ? clamp(v) : BOARD_ZOOM_DEFAULT;
  } catch {
    return BOARD_ZOOM_DEFAULT;
  }
}

function createBoardZoomStore() {
  const { subscribe, set: rawSet } = writable<number>(readStored());
  function set(value: number) {
    const next = clamp(value);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* ignore */ }
    rawSet(next);
  }
  return { subscribe, set };
}

export const boardZoom = createBoardZoomStore();

export function zoomBoardIn() {
  boardZoom.set(get(boardZoom) + BOARD_ZOOM_STEP);
}
export function zoomBoardOut() {
  boardZoom.set(get(boardZoom) - BOARD_ZOOM_STEP);
}
export function resetBoardZoom() {
  boardZoom.set(BOARD_ZOOM_DEFAULT);
}
